import { Injectable, OnDestroy } from '@angular/core';
import { CapgainDistributionService } from '@products/services/capgain-distribution.service';
import {
  CapGainFilterType,
  CapGainListing,
  CapGainListingFundStatus,
  CapGainListingTableDetails,
  CapGainsState,
} from '@products/types/tax.type';
import { Logger } from '@utils/logger';
import { Subject } from 'rxjs';
import capGainDistributionData from '@graphql/tax/us-capital-gains.graphql';
import { CapGainDistribution } from '@products/models/capgain-distribution';
import { Observable } from 'rxjs';
import sortedUniq from 'lodash/sortedUniq';
import cloneDeep from 'lodash/cloneDeep';
import { DropdownItem, PagedRecords } from '@frk/eds-components';
import { SiteConfigService } from '@services/site-config.service';
import { NA, SINGLECLASS } from '@products/utils/constants/product.constants';
import { TranslateService } from '@shared/translate/translate.service';
import { ConfigurationId, FundIdentifier } from '@types';
import { DebugService } from '@services/debug.service';
import { takeUntil } from 'rxjs/operators';
import { hasValue } from '@products/utils/mappers/mapper.utils';

const logger = Logger.getLogger('USCapitalGainsService');
const FILTER_TYPES: string[] = [
  'productType',
  'taxYear',
  'investmentGroup',
  'shareClassName',
]; // Item name must be equal to "field name" in 'CapGainListing'.
// NGC-9135: Order of options for product type filter.
const PRODUCT_TYPE_OPTIONS_ORDER: ConfigurationId[] = [
  ConfigurationId.MF,
  ConfigurationId.ETF,
  ConfigurationId.CEF,
  ConfigurationId.TIF,
  ConfigurationId.VIP,
  ConfigurationId.CIT,
];

@Injectable({
  providedIn: 'root',
})
export class UsCapitalGainsService implements OnDestroy {
  private state: CapGainsState = {
    capGainListing: [],
    gridListing: [],
    filters: [],
  };
  capGainsState: Subject<CapGainsState>;
  protected pagedRecords: PagedRecords<CapGainListing>;
  isFilterProcessing = false;
  private unsubscribe$: Subject<void> = new Subject<void>();
  /**
   * default pagesize for the tables
   */
  pageSize = 100;

  constructor(
    private capgainDistributionService: CapgainDistributionService,
    private siteConfigService: SiteConfigService,
    private translateService: TranslateService,
    private debugService: DebugService
  ) {
    this.pagedRecords = new PagedRecords<CapGainListing>(this.pageSize); // Page size set to '25', ref. by dev kit.
    this.capGainsState = new Subject();
  }

  /**
   * Call register method of cap gain distribution service.
   */
  public populate(): void {
    this.capgainDistributionService
      .register(capGainDistributionData)
      .subscribe((capGainDistributions) => {
        logger.debug(capGainDistributions);

        // NGC-9841: Filter out funds based upon status.
        // Here, remove inactive funds, while other status will be handled in template accordingly.
        this.state.capGainListing = this.formatCapGainDistributionsData(
          capGainDistributions.filter(
            (capGainDistribution) =>
              capGainDistribution.fundStatus !==
              CapGainListingFundStatus.inactive
          )
        );

        this.state.gridListing = this.state.capGainListing;

        // Extract filters data from the listing.
        this.state.filters = this.extractFiltersData(
          this.state.capGainListing,
          FILTER_TYPES
        );

        // Configure pagination.
        this.pagedRecords.records = this.state.capGainListing;

        // expand tables for Smarsh capture
        this.debugService
          .getExpandAllComponents$()
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((expandAll: boolean) => {
            if (expandAll) {
              this.pageSize = this.pagedRecords.expandAll();
            }
          });

        this.capGainsState.next(this.state);
      });
  }

  /**
   * Format the pds feed into feasible data structure.
   * @param capGainDistributions cap gain distributions feed
   */
  formatCapGainDistributionsData(
    capGainDistributions: CapGainDistribution[]
  ): CapGainListing[] {
    const capGainListing: CapGainListing[] = [];

    // 1. Sort with respect to fundName and shareClassName.
    const sortedCapGainDist: CapGainDistribution[] = capGainDistributions.sort(
      (a, b) => {
        const fundIdCompVal: number = a.fundName.localeCompare(b.fundName);

        if (fundIdCompVal === 0) {
          return a.shareClassName.localeCompare(b.shareClassName);
        }
        return fundIdCompVal;
      }
    );

    // 2. Group each distribution by share class code.
    sortedCapGainDist.forEach((capGainDistObj) => {
      // 3. Check if, already not present in listing group.
      const isAlreadyInList: boolean =
        typeof capGainListing.find(
          (capGainListObj) =>
            capGainListObj.fundId === capGainDistObj.fundId &&
            capGainListObj.shareClassCode === capGainDistObj.shareClassCode
        ) !== 'undefined';

      // 4. If yes, then there is different cap gains data for same fund id and share class code.
      // Push it into tableDetails array.
      if (isAlreadyInList) {
        capGainListing
          .find(
            (capGainListObj) =>
              capGainListObj.fundId === capGainDistObj.fundId &&
              capGainListObj.shareClassCode === capGainDistObj.shareClassCode
          )
          .tableDetails.push({
            capitalGainRecordDate: capGainDistObj.capitalGainRecordDate,
            capitalGainExDividendDate: capGainDistObj.capitalGainExDividendDate,
            capitalGainPayableDate: capGainDistObj.capitalGainPayableDate,
            capitalGainReInvestmentAmount:
              capGainDistObj.capitalGainReInvestmentAmount,
            longTermCapitalGainAmount: capGainDistObj.longTermCapitalGainAmount,
            shortTermCapitalGainAmount:
              capGainDistObj.shortTermCapitalGainAmount,
            totalDistributionAmountMonthly:
              capGainDistObj.totalDistributionAmountMonthly,
            estimateFlag: capGainDistObj.estimateFlag === 'true' ? true : false,
          });
      } else {
        const defaultIdentifierKey = this.siteConfigService.getDefaultIdentifierKey(
          capGainDistObj.productTaxonomy
        );
        const defaultIdentifier: FundIdentifier = defaultIdentifierKey
          ? (capGainDistObj[defaultIdentifierKey] as FundIdentifier)
          : null;

        const tableDetails: CapGainListingTableDetails = {
          capitalGainRecordDate: capGainDistObj.capitalGainRecordDate,
          capitalGainExDividendDate: capGainDistObj.capitalGainExDividendDate,
          capitalGainPayableDate: capGainDistObj.capitalGainPayableDate,
          capitalGainReInvestmentAmount:
            capGainDistObj.capitalGainReInvestmentAmount,
          longTermCapitalGainAmount: capGainDistObj.longTermCapitalGainAmount,
          shortTermCapitalGainAmount: capGainDistObj.shortTermCapitalGainAmount,
          totalDistributionAmountMonthly:
            capGainDistObj.totalDistributionAmountMonthly,
          estimateFlag: capGainDistObj.estimateFlag === 'true' ? true : false,
        };

        // 5. If no, it is new to the list, add the details accordingly.
        capGainListing.push({
          fundId: capGainDistObj.fundId,
          fundName: capGainDistObj.fundName,
          shareClassCode: capGainDistObj.shareClassCode,
          shareClassName: capGainDistObj.shareClassName,
          capGainFreq: capGainDistObj.capGainFrequency,
          productType: capGainDistObj.productType,
          taxYear: capGainDistObj.taxYear,
          cusip: capGainDistObj.cusip,
          ticker: capGainDistObj.ticker,
          tableDetails: [tableDetails],
          investmentGroup: capGainDistObj.investmentGroup,
          fundLink: this.siteConfigService.getFundLink(
            capGainDistObj.fundId,
            capGainDistObj.shareClassCode,
            capGainDistObj.fundName,
            defaultIdentifier
          ),
          fundStatus: capGainDistObj.fundStatus,
          // NGC-11266: Check for funds having no distributions and assign flag accordingly.
          hasNoDistributions: !Object.keys(tableDetails).some(
            (prop: keyof CapGainListingTableDetails) =>
              prop !== 'estimateFlag' && hasValue(tableDetails, prop)
          ),
          taId: capGainDistObj.taId,
        });
      }
    });

    return capGainListing;
  }

  /**
   * Extract the filters data as needed.
   * @param capGainListing formatted cap gain listing.
   * @param filterTypes filter type list
   */
  extractFiltersData(
    capGainListing: CapGainListing[],
    filterTypes: string[]
  ): CapGainFilterType[] {
    const capGainFilterTypes: CapGainFilterType[] = [];

    // 1. Loop through each filter type listed.
    filterTypes.forEach((filterType) => {
      // 2. Extract the unique values against the filter type above.
      //    Example, all product types values like, MF/CEF/TIF for the field 'productType'.
      //    So, first extract all values, sort them and then extract non-duplicate values.
      const filterTypeUniqueOptions: string[] = sortedUniq(
        capGainListing
          .map((capGainListingObj) => capGainListingObj[filterType])
          .sort((a: string, b: string) => a.localeCompare(b))
      );

      // 3. Now, remove 'SINGLCLASS' & 'NA' from shareClassName filter options, if present.
      //    First, convert all elements in lowercase and save as formatted list.
      //    Then, fetch index against the matched one and splice it from the array.
      if (filterType === 'shareClassName') {
        [SINGLECLASS.toLowerCase(), NA.toLowerCase()].forEach(
          (shareClassName) => {
            const formattedFilterTypeUniqueOptions = filterTypeUniqueOptions.map(
              (filterTypeUniqueOption: string) =>
                filterTypeUniqueOption.toLowerCase()
            );
            const index = formattedFilterTypeUniqueOptions.indexOf(
              shareClassName
            );

            if (index !== -1) {
              filterTypeUniqueOptions.splice(index, 1);
            }
          }
        );
      }

      // 4. Create the options list by mapping different required field values.
      let capGainFilterTypeOptions: DropdownItem[] = [];
      filterTypeUniqueOptions.map((uniqueOption) => {
        capGainFilterTypeOptions.push({
          label:
            filterType === 'productType'
              ? this.translateService.instant(
                  `products.capgains-filter-producttype-${uniqueOption
                    .toLowerCase()
                    .split(' ')
                    .join('-')}`
                )
              : uniqueOption,
          value: uniqueOption,
          checked: false,
        });
      });

      // 5. Sort again, for 'filterType' as 'productType'.
      //    As per the order defined manually.
      if (filterType === 'productType') {
        const sortedFilterTypeOptions: DropdownItem[] = [];

        // ***** NGC-9135: Made product type filter options to be order as defined manually. *****
        PRODUCT_TYPE_OPTIONS_ORDER.forEach((configurationId) => {
          const capGainFilterTypeOption = capGainFilterTypeOptions.find(
            (option) => option.value === configurationId
          );

          if (capGainFilterTypeOption) {
            sortedFilterTypeOptions.push(capGainFilterTypeOption);
          }
        });

        // Fallback for Configuration Ids present in 'PDS feed' and not in 'PRODUCT_TYPE_OPTIONS_ORDER'.
        if (
          sortedFilterTypeOptions.length !== capGainFilterTypeOptions.length
        ) {
          // Loop all configuration ids present in pds feed.
          capGainFilterTypeOptions.forEach((option) => {
            if (
              // The one which is not found in sorted one, is the option to be inserted at last.
              // As its priority isn't defined in 'PRODUCT_TYPE_OPTIONS_ORDER'.
              !sortedFilterTypeOptions.find(
                (sortedOption) => sortedOption.value === option.value
              )
            ) {
              sortedFilterTypeOptions.push(option);
            }
          });
        }

        capGainFilterTypeOptions = sortedFilterTypeOptions;
      }

      // 6. Finally, push the created filter type object to the list.
      capGainFilterTypes.push({
        filterLabel: filterType.toLocaleLowerCase(), // Intentionally made lower case, to use it as filter label in kebab-case.
        filterName: filterType,
        filterOptions: capGainFilterTypeOptions,
      });
    });

    return capGainFilterTypes;
  }

  /**
   * Filter the listing based upon the filter type selected as well as the search text.
   * @param searchValue search text if entered
   */
  filterListing(searchValue?: string): void {
    this.isFilterProcessing = true;
    let filteredCapGainListing: CapGainListing[] = cloneDeep(
      this.state.capGainListing
    );

    // 1. Loop through each filter type.
    this.state.filters.forEach((filterType) => {
      // 2. For each filter type, extract all the selected options.
      const selections: string[] = filterType.filterOptions
        .filter((filterOption) => filterOption.checked === true)
        .map((filterOption) => filterOption.value);

      // 3. Filter grid by filter type & its selected options.
      //    Save the filtered list into the same variable.
      if (selections.length) {
        filteredCapGainListing = filteredCapGainListing.filter(
          (capGainListingObj) =>
            selections.includes(capGainListingObj[filterType.filterName])
        );
      }
    });

    // 4. Filter on basis of search value, if present.
    if (searchValue?.trim()) {
      filteredCapGainListing = filteredCapGainListing.filter(
        (capGainListingObj) =>
          capGainListingObj.fundName
            .toLowerCase()
            .includes(searchValue.toLowerCase()) ||
          capGainListingObj.fundId === searchValue ||
          capGainListingObj.cusip.toLowerCase() === searchValue.toLowerCase() ||
          capGainListingObj.ticker.toLowerCase() ===
            searchValue.toLowerCase() ||
          capGainListingObj.taId === searchValue
      );
    }

    // 5. Save the final filtered listing to grid listing.
    this.state.gridListing = filteredCapGainListing;

    // 6. Update pagination.
    this.pagedRecords.records = this.state.gridListing;

    // 7. Assign boolean, if current gridListing doesn't have share class for any row.
    // NGC-9140: Remove class fund filter when no row has the same.
    this.state.hasNoShareClass = this.hasNoShareClass(this.state.gridListing);

    // 8. Update the new state.
    this.capGainsState.next(this.state);
    this.isFilterProcessing = false;
  }

  /**
   * Check if, listing doesn't have share class for any row.
   * @param gridListing filtered grid listing
   */
  hasNoShareClass(gridListing: CapGainListing[]): boolean {
    return !gridListing.some(
      (gridListingObj) =>
        gridListingObj.shareClassCode &&
        gridListingObj.shareClassCode !== SINGLECLASS &&
        gridListingObj.shareClassCode !== NA
    );
  }

  /**
   * ******************** Pagination helper methods ********************
   */

  /**
   * Return total number of pages as observable.
   */
  getNumberOfPages$(): Observable<number> {
    return this.pagedRecords.numberOfPages$;
  }

  /**
   * Return current page number as observable.
   */
  getPageNumber$(): Observable<number> {
    return this.pagedRecords.pageNumber$;
  }

  /**
   * Return current page records as observable.
   */
  getCurrentPage$(): Observable<CapGainListing[]> {
    return this.pagedRecords.currentPage$;
  }

  /**
   * Trigger page number selection accordingly.
   * @param n page number selected
   */
  goToPage(n: number): void {
    if (this.pagedRecords) {
      this.pagedRecords.pageNumber = n;
    }
  }

  /**
   * Get page size for the list.
   */
  getPageSize(): number {
    return this.pagedRecords.pageSize;
  }

  /**
   * Set new page size for the list.
   * @param size rows limit in one page.
   */
  setPageSize(size: number): void {
    this.pagedRecords.pageSize = size;
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
